import { useEffect, useState } from 'react';
import { fetchImageProcessingResult } from 'src/redux/api/inventory/imageProcessing';
import { ImageProcessingBatchItem } from 'src/redux/api/inventory/imageProcessingBatches';

type Params = {
  vehicleId: number;
  item?: ImageProcessingBatchItem;
};

/**
 * `item.result_url` points at an authenticated API route, so it can't be used directly as an
 * image source: the browser would request it without the session headers and get a 401. The
 * result has to be fetched as a blob and exposed through a temporary object URL.
 */
const useBatchResultPreview = ({ vehicleId, item }: Params) => {
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const mediaId = item?.media_id;
  const jobId = item?.job_id;

  useEffect(() => {
    if (!mediaId || !jobId) {
      setPreviewUrl(undefined);
      setHasFailed(false);
      return undefined;
    }

    let isCurrent = true;
    let objectUrl: string | undefined;

    // Drop the previous result right away, otherwise the image being replaced stays painted
    // under the spinner and reads as if it belonged to the item just navigated to.
    setPreviewUrl(undefined);
    setIsLoading(true);
    setHasFailed(false);

    void fetchImageProcessingResult({ vehicleId, mediaId, jobId })
      .then((blob) => {
        if (!isCurrent) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (isCurrent) {
          setHasFailed(true);
          setPreviewUrl(undefined);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [jobId, mediaId, vehicleId]);

  return { previewUrl, isLoading, hasFailed };
};

export default useBatchResultPreview;
